import React, { Fragment, useEffect, useState } from 'react';
import Footer from '../Footer/Footer';
import Loader from '../../loader/Loader';
import PageSEO from '../Common/PageSEO';
import {
    FeatureHero,
    FeatureHeroCta,
    FeatureStats,
    FeatureSection,
    FeatureDownloadCta
} from '../Common/FeatureContent';
import { useLocale } from '../../localization/LocaleContext';
import { useCurrency } from '../../localization/CurrencyContext';
import { ROUTE_PATHS } from '../../localization/constants';
import './PaymentsMain.css';

const MOBILE_ICON = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
        <line x1="12" y1="18" x2="12.01" y2="18"/>
    </svg>
);

const ACCOUNT_ICON = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="14" rx="2" ry="2"/>
        <line x1="2" y1="10" x2="22" y2="10"/>
    </svg>
);

const APPLE_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M16.365 1.43c0 1.14-.42 2.2-1.26 3.03-.9.9-1.98 1.42-3.13 1.33-.05-1.1.44-2.24 1.24-3.05.86-.87 2.06-1.4 3.15-1.31zM20.5 17.2c-.55 1.27-.82 1.84-1.53 2.96-.99 1.56-2.38 3.5-4.11 3.51-1.53.02-1.93-1-4.01-.99-2.08.01-2.52 1.01-4.05.99-1.73-.02-3.05-1.77-4.04-3.33C-.02 16.6-.32 11.5 1.4 8.83c1.22-1.9 3.15-3.01 4.96-3.01 1.84 0 3 1.01 4.52 1.01 1.48 0 2.38-1.01 4.51-1.01 1.61 0 3.32.88 4.54 2.4-3.99 2.19-3.34 7.9.57 8.98z"/>
    </svg>
);

const GOOGLE_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.86c2.26-2.09 3.56-5.17 3.56-8.87z"/>
        <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z"/>
        <path fill="#FBBC05" d="M5.27 14.29a7.2 7.2 0 0 1 0-4.58V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09z"/>
        <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.7 0 3.99 2.47 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"/>
    </svg>
);

const LOCK_ICON = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
);

const CHECK_ICON = (
    <svg className="pay-bullet-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);

const TREND_UP_ICON = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
        <polyline points="17 6 23 6 23 12"/>
    </svg>
);

const ALERT_ICON = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
);

const CHECKOUT_ITEM_COUNT = 5;
const CHECKOUT_ROTATE_MS = 3200;

function PaymentsMain(){
    const { t, locale } = useLocale();
    const { formatCurrency } = useCurrency();
    const p = (key) => t(`featurePages.payments.${key}`);

    const[loader, SetLoader] = useState(false)
    const [checkoutIndex, setCheckoutIndex] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            SetLoader(true)
        }, 1000)

        return () => clearTimeout(timer)
    }, [])

    useEffect(() => {
        const interval = setInterval(() => {
            setCheckoutIndex((prev) => (prev + 1) % CHECKOUT_ITEM_COUNT);
        }, CHECKOUT_ROTATE_MS);

        return () => clearInterval(interval);
    }, [])

    if (!loader) {
        return <Loader/>;
    }

    const checkoutItems = Array.from({ length: CHECKOUT_ITEM_COUNT }, (_, i) => ({
        label: p(`checkoutItem${i + 1}`),
        amount: formatCurrency(p(`checkoutAmount${i + 1}`))
    }));
    const currentCheckoutItem = checkoutItems[checkoutIndex];

    const stats = [
        { label: p('statPaidLabel'), value: p('statPaidValue'), note: p('statPaidNote'), icon: TREND_UP_ICON, tone: 'paid' },
        { label: p('statOutstandingLabel'), value: p('statOutstandingValue'), note: p('statOutstandingNote'), tone: 'outstanding' },
        { label: p('statOverdueLabel'), value: p('statOverdueValue'), note: p('statOverdueNote'), icon: ALERT_ICON, tone: 'overdue' }
    ];

    const bullets = [p('method2Bullet1'), p('method2Bullet2'), p('method2Bullet3')];

    return(
        <Fragment>
            <PageSEO title={p('metaTitle')} description={p('metaDescription')} path={ROUTE_PATHS.payments} locale={locale} />

            <FeatureHero
                className="pay-hero"
                eyebrow={p('eyebrow')}
                title={p('heroTitle')}
                highlight={p('heroHighlight')}
                subtitle={p('heroSubtitle')}
                cta={
                    <FeatureHeroCta
                        primaryLabel={t('shared.featureStartFree')}
                        primaryTarget="feature-download"
                        secondaryLabel={t('shared.featureSeeHowItWorks')}
                        secondaryTarget="feature-content"
                    />
                }
            />

            <FeatureSection className="pay-stats-section" title={p('sectionTitle')}>
                <FeatureStats stats={stats} />
            </FeatureSection>

            {/* Payment methods section */}
            <section id="feature-content" className="pay-methods-section">
                <div className="container">
                    <div className="fc-section-header">
                        <h2 className="h-1 h-bold fc-section-title">{p('methodsSectionTitle')}</h2>
                        <p className="fc-section-description">{p('methodsSectionDescription')}</p>
                    </div>

                    <div className="pay-methods-grid">
                        {/* Left: benefits */}
                        <div className="pay-benefits">
                            <div className="pay-benefit">
                                <div className="pay-benefit-icon">{MOBILE_ICON}</div>
                                <div>
                                    <h3 className="pay-benefit-title">{p('method1Title')}</h3>
                                    <p className="pay-benefit-desc">{p('method1Description')}</p>
                                </div>
                            </div>

                            <div className="pay-benefit pay-benefit-highlight">
                                <div className="pay-benefit-icon">{ACCOUNT_ICON}</div>
                                <div>
                                    <span className="pay-badge">{p('method2Badge')}</span>
                                    <h3 className="pay-benefit-title">{p('method2Title')}</h3>
                                    <p className="pay-benefit-desc">{p('method2Description')}</p>
                                    <ul className="pay-bullets">
                                        {bullets.map((bullet, idx) => (
                                            <li className="pay-bullet" key={idx}>
                                                {CHECK_ICON}
                                                <span>{bullet}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Right: static checkout card */}
                        <div className="pay-checkout-wrapper">
                            <div className="pay-checkout-glow" aria-hidden="true"></div>
                            <div className="pay-checkout-card">
                                <div className="pay-checkout-header">
                                    <div className="pay-checkout-header-left">
                                        <p className="pay-checkout-invoice">{p('checkoutInvoiceLabel')}</p>
                                        <p className="pay-checkout-item" key={checkoutIndex}>
                                            {currentCheckoutItem.label}
                                        </p>
                                    </div>
                                    <div className="pay-checkout-total-block">
                                        <p className="pay-checkout-total-label">{p('checkoutTotalLabel')}</p>
                                        <p className="pay-checkout-total" key={checkoutIndex}>
                                            {currentCheckoutItem.amount}
                                        </p>
                                    </div>
                                </div>

                                <p className="pay-checkout-express">{p('checkoutExpressLabel')}</p>

                                <div className="pay-wallet-btn pay-wallet-btn-apple">
                                    {APPLE_ICON}
                                    <span>{p('checkoutApplePay')}</span>
                                </div>
                                <div className="pay-wallet-btn pay-wallet-btn-google">
                                    {GOOGLE_ICON}
                                    <span>{p('checkoutGooglePay')}</span>
                                </div>

                                <div className="pay-divider">
                                    <span className="pay-divider-line"></span>
                                    <span className="pay-divider-text">{p('checkoutOrCardLabel')}</span>
                                    <span className="pay-divider-line"></span>
                                </div>

                                <input className="pay-input" type="text" placeholder={p('checkoutCardNumber')} readOnly tabIndex={-1} />
                                <div className="pay-input-row">
                                    <input className="pay-input" type="text" placeholder={p('checkoutExpiry')} readOnly tabIndex={-1} />
                                    <input className="pay-input" type="text" placeholder={p('checkoutCvc')} readOnly tabIndex={-1} />
                                </div>

                                <button type="button" className="pay-pay-btn" tabIndex={-1}>
                                    {t('featurePages.payments.checkoutPayButton', { amount: currentCheckoutItem.amount })}
                                </button>

                                <div className="pay-checkout-footer">
                                    {LOCK_ICON}
                                    <span className="pay-checkout-footer-text">{p('checkoutPoweredBy')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <FeatureDownloadCta
                title={p('ctaTitle')}
                description={p('ctaDescription')}
                ctaLabel={p('ctaButton')}
                storesLabel={t('shared.featureDownloadApp')}
            />

            <Footer />
        </Fragment>
    )
}

export default PaymentsMain;